/**
 * 权限相关用户提示（与 backend/utils/permissionCatalogMeta.js 展示名对齐）。
 */

const SEGMENT_LABELS = Object.freeze({
	create: '新建',
	update: '更新',
	delete: '删除',
	query: '查询',
	list: '列表',
	view: '查看',
	edit: '编辑',
	initiate: '发起',
	execute: '执行',
	confirm: '确认',
	adjust: '调整',
	apply: '应用',
	delivery: '发货',
	accept: '验收',
	receive: '收款',
	export: '导出',
	draft: '草稿',
	quotation: '报价单',
	approver: '审批人',
	applicant: '申请人',
	assistant: '助理',
	customer: '客户',
	order: '订单',
	quota: '额度',
	department: '部门',
	group: '小组',
	bulkRematch: '批量重匹配',
	finalCost: '最终成本',
	purchasingPrice: '进价',
	minimumSellingPrice: '底价',
	statementPdf: '对账单',
	homeCharts: '首页图表',
	dashboardCharts: '统计图表',
	layout: '布局',
	approving: '待审批',
	prepayment: '预付款',
	contract: '待签约',
	delivering: '发货中',
	accepting: '验收中',
	ar: '应收账款',
	appling: '申请待办',
	rejected: '被驳回',
	processing: '进程',
	final_cost_verification: '待核验',
	stat_cards: '统计卡片',
	home_charts: '经营看板',
	overdue: '超期统计',
	all: '全公司',
	menu: '菜单',
	dashboard: '仪表盘',
	statistics: '统计',
	performance: '绩效',
	approval: '审批',
	notification: '通知',
	profile: '个人资料',
	loginlog: '登录日志',
	product: '产品',
	brand: '品牌',
	supplier: '供应商',
	user: '用户',
});

/** 常用权限点中文名（路由与高频 API） */
const PERMISSION_LABELS = Object.freeze({
	'erp.menu.dashboard.stat_cards': '统计卡片',
	'erp.menu.dashboard.home_charts': '经营看板',
	'erp.menu.dashboard.overdue': '超期统计',
	'erp.menu.dashboard.menu': '菜单',
	'erp.menu.dashboard.search': '全局搜索',
	'erp.menu.order.create': '新建订单',
	'erp.menu.order.draft': '订单草稿',
	'erp.menu.order.list': '订单查询',
	'erp.menu.order.quotation': '报价单',
	'erp.menu.statistics.view': '统计/探索分析',
	'erp.menu.customer.list': '客户列表',
	'erp.menu.product.list': '产品列表',
	'erp.menu.profile': '个人资料',
	'erp.menu.performance.list': '绩效规则',
	'erp.menu.approval.applications': '申请记录',
	'erp.menu.approval.records': '审批记录',
	'erp.menu.notification.list': '通知列表',
	'erp.menu.user.list': '用户管理',
	'erp.menu.loginlog.list': '登录日志',
	'erp.menu.brand.list': '品牌管理',
	'erp.menu.supplier.list': '供应商管理',
	'erp.order.query.tenantAll': '全部订单',
	'erp.approval.query.tenantAll': '查询待审批（全公司）',
	'erp.order.draft.query.tenantAll': '全部草稿',
	'erp.order.query.all': '跨公司全量订单（平台级）',
	'erp.order.draft.query.all': '跨公司全量草稿（平台级）',
	'erp.dashboard.order.approving': '待办 Tab · 待审批',
	'erp.dashboard.order.prepayment': '待办 Tab · 预付款',
	'erp.dashboard.order.contract': '待办 Tab · 待签约',
	'erp.dashboard.order.delivering': '待办 Tab · 发货中',
	'erp.dashboard.order.accepting': '待办 Tab · 验收中',
	'erp.dashboard.order.ar': '待办 Tab · 应收账款',
	'erp.dashboard.order.appling': '待办 Tab · 申请中',
	'erp.dashboard.order.rejected': '待办 Tab · 被驳回',
	'erp.dashboard.order.processing.buyer': '待办 Tab · 进程(采购)',
	'erp.dashboard.order.processing.finance': '待办 Tab · 进程(财务)',
	'erp.dashboard.order.final_cost_verification': '待办 Tab · 待核验',
	'erp.user.query': '查询用户',
	'erp.user.update': '更新用户',
	'erp.performance.bulkRematch': '批量重匹配绩效',
	'erp.brand.create': '新建品牌',
	'erp.product.query': '查询产品',
	'erp.order.query': '订单查询',
});

export const humanizePermissionKey = (key) => {
	const parts = String(key || '')
		.replace(/^erp\./, '')
		.split('.');
	const labels = parts.map((p) => SEGMENT_LABELS[p] || p).filter(Boolean);
	return labels.length ? labels.join(' · ') : key;
};

export const getPermissionDisplayName = (key) => PERMISSION_LABELS[key] || humanizePermissionKey(key);

export const formatPermissionLabels = (keys) => {
	const arr = Array.isArray(keys) ? keys : [keys];
	return arr.filter(Boolean).map((k) => `「${getPermissionDisplayName(k)}」`).join('、');
};

/** 路由守卫：无菜单/页面权限 */
export const formatRouteDeniedMessage = (permissionKeys) =>
	`您暂无权限访问${formatPermissionLabels(permissionKeys)}，请联系管理员开通`;

/** 从 RTK Query / fetch 错误提取 403 文案 */
export const parseApiForbiddenMessage = (error) => {
	const msg = error?.data?.message;
	if (typeof msg === 'string' && msg.trim()) {
		return msg.trim();
	}
	return '您暂无权限执行此操作，请联系管理员';
};

export const isForbiddenError = (error) => {
	const status = error?.status ?? error?.originalStatus;
	return status === 403;
};

/** 通用 API 错误文案（含 403 / 401） */
export const getApiErrorMessage = (error, options = {}) => {
	const { fallback = '操作失败，请稍后重试' } = options;
	const status = error?.status ?? error?.originalStatus;
	const msg = error?.data?.message || error?.error;

	if (status === 403) return parseApiForbiddenMessage(error);
	if (status === 401) return msg || '登录已失效，请重新登录';
	if (status === 404) return msg || '请求的资源不存在';
	if (status === 'FETCH_ERROR') return '网络连接失败，请检查网络后重试';
	if (typeof msg === 'string' && msg.trim()) return msg.trim();
	return fallback;
};
